import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PaidServiceOption,
  PaymentMethodEnum
} from '@/__generated_graphql__/types';
import { ClickableChip } from '@/components/ui/ClickableChip/ClickableChip';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import {
  MAX_TOPUP_CARD_AMOUNT_PORTMANAT,
  MAX_TOPUP_CARD_AMOUNT_YIGIM
} from '@/constants/common';
import { PAYMENT_MODAL_CY } from '@/constants/test-id';

import { extractTopUpAmount } from '../../helpers/extractTopUpAmount';
import * as S from './DepositSelector.styles';
import { MAX_NUMBER_LENGTH } from './ExtraContent.constants';
import {
  formatNumberWithSpaces,
  getPaidService,
  isValidDepositRange
} from './ExtraContent.helpers';
import { ExtraContentModalProps } from './ExtraContent.types';

type Props = {
  balance?: number;
  options: PaidServiceOption[];
  setActivePaidServiceId: ExtraContentModalProps['setActivePaidServiceId'];
  setIsValidPrice?: (isValid: boolean) => void;
  selectedPaymentWay: string;
};

const { AMOUNT_CHIP, DEPOSIT_INPUT } = PAYMENT_MODAL_CY;

export const DepositSelector: FC<Props> = ({
  balance = 0,
  options,
  setActivePaidServiceId,
  setIsValidPrice,
  selectedPaymentWay
}) => {
  const { t } = useTranslation();
  const [selectedChip, setSelectedChip] = useState<PaidServiceOption | null>(
    null
  );
  const [customPrice, setCustomPrice] = useState<number>(0);

  const selectedPrice: number = selectedChip?.price || customPrice;
  const isPortmanat = selectedPaymentWay === PaymentMethodEnum.Portmanat;
  const maxValue = isPortmanat
    ? MAX_TOPUP_CARD_AMOUNT_PORTMANAT
    : MAX_TOPUP_CARD_AMOUNT_YIGIM;

  const isMax = String(selectedPrice).length >= MAX_NUMBER_LENGTH;
  const isShowError = selectedPrice > maxValue;

  const handleChangePrice = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    value = value.replace(/[^\d]/g, '');

    const num = Number(value);

    const prev = String(customPrice);
    const isDeleting = value.length < prev.length;

    if (isMax && !isDeleting) {
      return;
    }

    if (!Number.isNaN(num)) {
      setCustomPrice(num);

      const customOption = options.find(item => !item.price);
      setSelectedChip(current => customOption ?? current);
    }
  };

  const handleSelectChip = (option: PaidServiceOption) => {
    setSelectedChip(option);
    if (option?.price) setCustomPrice(option.price);
  };

  useEffect(() => {
    const activePaidService = getPaidService(selectedChip, customPrice);
    setActivePaidServiceId(activePaidService);

    if (setIsValidPrice) {
      const extractedPrice = extractTopUpAmount(activePaidService);
      const isValid = isValidDepositRange(extractedPrice, maxValue);
      setIsValidPrice(isValid);
    }
  }, [setActivePaidServiceId, selectedChip, customPrice, selectedPaymentWay]);

  const computedBalance: number = balance + selectedPrice;

  const computedTextType = {
    text: isShowError
      ? 'InsufficientBalanceModal.top_up_balance_error'
      : 'InsufficientBalanceModal.after_top_up',
    amount: isShowError ? maxValue : computedBalance
  };

  const balanceText = t(computedTextType.text, {
    balance: computedTextType.amount
  });

  const charactersLength = String(selectedPrice).length;

  return (
    <S.DepositCard>
      <Typography variant="title4">
        {t('InsufficientBalanceModal.selector_title')}
      </Typography>

      <S.DepositContainer>
        <S.Balance>
          <S.BalanceInput
            $charactersLength={charactersLength}
            onChange={handleChangePrice}
            value={formatNumberWithSpaces(selectedPrice)}
            data-cy={DEPOSIT_INPUT}
          />
          <Typography variant="h0">₼</Typography>
        </S.Balance>
        <S.AfterTopUpText variant="title5" $isDanger={isShowError}>
          {balanceText}
        </S.AfterTopUpText>
      </S.DepositContainer>

      <S.ChipsWrapper>
        {options.map(item => (
          <ClickableChip
            key={item.id}
            id={String(item.id)}
            label={item.name}
            isSelected={item.id === selectedChip?.id}
            isPremium
            data-cy={AMOUNT_CHIP(item.id)}
            onClick={() => handleSelectChip(item)}
          />
        ))}
      </S.ChipsWrapper>
    </S.DepositCard>
  );
};
